import React from 'react';
import { Mail, MessageSquare, Calendar, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { Link, useNavigate } from 'react-router-dom';

interface TeamMember {
  id: string;
  displayName: string;
  email: string;
  role: 'admin' | 'staff';
  department?: string;
  title?: string;
  status: 'online' | 'away' | 'offline';
  lastActive?: string;
  joinedAt: string;
}

interface TeamMemberCardProps {
  member: TeamMember;
}

export default function TeamMemberCard({ member }: TeamMemberCardProps) {
  const navigate = useNavigate();

  const getStatusColor = (status: TeamMember['status']) => {
    switch (status) {
      case 'online':
        return 'bg-success-DEFAULT';
      case 'away':
        return 'bg-warning-DEFAULT';
      default:
        return 'bg-text-muted';
    }
  };

  const handleStartChat = () => {
    // Navigate to messages page with the user's ID as a query parameter
    navigate(`/messages?userId=${member.id}`);
  };

  return (
    <div className="bg-surface-light rounded-xl shadow-sm p-6">
      <div className="flex items-start gap-4">
        {/* Avatar */}
        <div className="relative">
          <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
            <span className="text-lg font-medium text-primary-700">
              {member.displayName.split(' ').map(n => n[0]).join('')}
            </span>
          </div>
          <div
            className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 
                     border-surface-light ${getStatusColor(member.status)}`}
          />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-text-primary truncate">{member.displayName}</h3>
          <p className="text-sm text-text-secondary">{member.title || member.role}</p>
          {member.department && (
            <p className="text-sm text-text-muted">{member.department}</p>
          )}
        </div>
      </div>

      {/* Last Active */}
      {member.lastActive && (
        <div className="flex items-center gap-2 mt-4 text-sm text-text-secondary">
          <Clock className="w-4 h-4" />
          <span>Last active {format(new Date(member.lastActive), 'PP')}</span>
        </div>
      )}

      {/* Actions */}
      <div className="grid grid-cols-3 gap-2 mt-4">
        <a
          href={`mailto:${member.email}`}
          className="flex items-center justify-center p-2 text-text-secondary 
                   hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"
          title="Send email"
        >
          <Mail className="w-5 h-5" />
        </a>
        <button
          onClick={handleStartChat}
          className="flex items-center justify-center p-2 text-text-secondary 
                   hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"
          title="Start chat"
        >
          <MessageSquare className="w-5 h-5" />
        </button>
        <Link
          to={`/calendar?with=${member.id}`}
          className="flex items-center justify-center p-2 text-text-secondary 
                   hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"
          title="View calendar"
        >
          <Calendar className="w-5 h-5" />
        </Link>
      </div>
    </div>
  );
}